/**
 * kit-cart-edit.js
 * -----------------------------------------------------------------------------
 * Adds an "Edit design" link to any cart line that came from Kit Studio.
 *
 * The Liquid snippet (snippets/kit-cart-edit.liquid) is the tidier way to do
 * this and should be preferred where the theme allows it. It needs a `{% render %}`
 * inside the loop that draws a cart line, and on themes built from theme blocks
 * that loop is inside a private block a merchant may not be able to edit.
 * Heritage is one of those: main-cart.liquid contains no loop at all, only
 * `content_for 'block', type: '_cart-products'`.
 *
 * So this does the same job from the outside: read the cart, find the line
 * elements, append the link.
 *
 * Load it from layout/theme.liquid, NOT from the cart section:
 *
 *     <script src="{{ 'kit-cart-edit.js' | asset_url }}" defer></script>
 *
 * Most themes show the cart in a drawer that opens over whatever page the
 * shopper is on, and never render the cart section at all. Included from
 * main-cart.liquid this file only runs on /cart, which is the one place a
 * shopper using the drawer never visits.
 *
 * Does nothing at all when no line carries `_kit_edit`, so it is inert on an
 * ordinary cart and on every page that has no cart UI.
 */
(function () {
  'use strict';

  var STYLE_ID = 'kit-cart-edit-style';
  var CLASS = 'kit-cart-edit';

  /**
   * Where inside a row the link goes, tried in order. The row itself is the
   * fallback: on a table layout appending there lands in the last cell, which
   * is the price or the bin icon rather than the design details.
   */
  var TARGETS = ['td.cart-items__details', '.cart-item__details', '.cart-item__name'];

  /**
   * Candidate selectors for "one element per cart line", most specific first.
   * A theme is only accepted when its count matches the cart exactly, which is
   * what makes matching by position safe: any selector that catches a stray
   * element, or misses one, is rejected rather than putting an edit link on the
   * wrong garment.
   */
  var CANDIDATES = [
    // Heritage / Horizon: the drawer and the cart page both render lines as
    // table rows. Confirmed against the live theme, not guessed.
    'tr.cart-items__table-row',
    'cart-item',
    '.cart-item',
    '[data-line-item]',
    '[data-line-index]',
    '.cart-items__item',
    '.cart-product',
    '.cart-drawer__item',
    '.cart-drawer-item',
    '.drawer__item',
    '.cart__item',
    'tr.cart-item',
    'li.cart-item',
  ];

  function label() {
    var lang = (document.documentElement.lang || '').toLowerCase();
    return lang.indexOf('fr') === 0 ? 'Modifier' : 'Edit design';
  }

  /** Shown for the moment between the click and the page changing. */
  function editingLabel() {
    var lang = (document.documentElement.lang || '').toLowerCase();
    return lang.indexOf('fr') === 0 ? 'Ouverture…' : 'Opening…';
  }

  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent =
      '.' + CLASS + '{display:inline-block;margin-top:4px;font-size:.85em;text-decoration:underline;cursor:pointer}';
    document.head.appendChild(el);
  }

  /**
   * Every element that could be a cart row, from any selector, de-duplicated.
   * No count check: requiring the match to equal the number of cart lines meant
   * a single stray match anywhere on the page silently disabled the whole
   * feature, which is why the link showed up with one item in the drawer and
   * vanished with two.
   */
  function candidateRows() {
    var seen = [];
    for (var i = 0; i < CANDIDATES.length; i++) {
      var found = document.querySelectorAll(CANDIDATES[i]);
      for (var j = 0; j < found.length; j++) {
        if (seen.indexOf(found[j]) === -1) seen.push(found[j]);
      }
    }
    // Drop any row that contains another row: on a table the <tr> and a
    // wrapper can both match, and only the innermost is the real line.
    return seen.filter(function (node) {
      return !seen.some(function (other) {
        return other !== node && node.contains(other);
      });
    });
  }

  /** The variant id a row points at, read off its product link. */
  function variantOf(node) {
    var link = node.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var m = /[?&]variant=(\d+)/.exec(link.getAttribute('href') || '');
    return m ? m[1] : null;
  }

  /**
   * Remove a cart line, then hand back whether it went.
   *
   * Called at the moment the shopper clicks Edit, which is the one moment the
   * key is guaranteed fresh: it was read from a cart that rendered seconds ago.
   * Doing it here rather than after the re-add is what stops the duplicate. The
   * old order, add the new line then drop the old one, depended on a key that
   * had been sitting in a URL while the shopper worked, and a Shopify line key
   * is a hash of the line's properties: anything that touched that line in the
   * meantime invalidated it, /cart/change.js 404'd, and the shopper ended up
   * holding both versions.
   */
  function dropLine(key, done) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
      .then(function (r) { done(r.ok); })
      .catch(function () { done(false); });
  }

  function paint(cart) {
    var configured = cart.items.filter(function (item) {
      return (item.properties || {})._kit_edit;
    });
    // Nothing configured in this cart: leave the DOM completely alone.
    if (!configured.length) return;

    var rows = candidateRows();
    if (!rows.length) return;

    // Match each row to its cart line by variant, so counts never matter and a
    // cart holding ordinary products alongside configured ones works. Two
    // lines can share a variant with different designs, so each row is
    // consumed once, in order.
    var byVariant = {};
    cart.items.forEach(function (item, index) {
      var v = String(item.variant_id);
      (byVariant[v] = byVariant[v] || []).push({ item: item, index: index });
    });

    /* Position is the fallback, and it matters more than it looks.
       Matching purely on the variant read out of a row's product link fails in
       two ordinary situations: a theme that links to `/products/handle` with no
       `?variant=` at all, and a second row whose variant queue the first row
       already emptied. Either one skips that row in silence, which is the
       "sometimes there is no Edit design link" report. When the rows and the
       cart lines line up one to one, which is every cart that is only Kit
       Studio items, their order is the cart's order and that is a better
       answer than a link that may not carry an id. */
    var positional = rows.length === cart.items.length;

    ensureStyle();
    rows.forEach(function (row, rowIndex) {
      var entry = null;
      var v = variantOf(row);
      var queue = v && byVariant[v];
      if (queue && queue.length) entry = queue.shift();
      else if (positional) entry = { item: cart.items[rowIndex], index: rowIndex };
      if (!entry || !entry.item) return;
      var url = (entry.item.properties || {})._kit_edit;
      if (!url) return;
      if (row.querySelector('.' + CLASS)) return; // already painted

      var host = row;
      for (var t = 0; t < TARGETS.length; t++) {
        var inner = row.querySelector(TARGETS[t]);
        if (inner) { host = inner; break; }
      }
      var a = document.createElement('a');
      a.className = CLASS;
      var key = entry.item.key;
      // The href still carries `line`, so the link works without JavaScript and
      // the configurator can fall back to removing the line itself.
      a.href = url + '&line=' + encodeURIComponent(key);
      a.textContent = label();
      a.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // open in a new tab
        e.preventDefault();
        a.textContent = editingLabel();
        dropLine(key, function (ok) {
          // Removed here, so the configurator has nothing left to do and Save
          // is an ordinary add. If it did NOT go, keep `line` on the URL and
          // let the configurator try, which is the old path and still works.
          window.location.href = ok ? url : a.getAttribute('href');
        });
      });
      host.appendChild(a);
    });
  }

  function run() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(paint)
      .catch(function () {
        /* no cart on this page, or offline. Nothing to add. */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  // Repaint when the cart UI changes. Listening only is not enough: a drawer is
  // usually injected or re-rendered when it opens, well after DOMContentLoaded,
  // and quantity changes replace the lines outright, throwing the links away.
  ['cart:update', 'cart:refresh', 'cart:change', 'cart:updated', 'kit:added'].forEach(function (name) {
    document.addEventListener(name, function () {
      setTimeout(run, 150);
    });
  });

  /**
   * Watch for cart markup appearing. Debounced, and it only re-reads the cart
   * when a mutation actually added an element that looks like a cart line, so
   * an unrelated busy page does not turn into a stream of /cart.js requests.
   */
  var pending = null;
  var observer = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (!looksLikeCart(node)) continue;
        clearTimeout(pending);
        pending = setTimeout(run, 200);
        return;
      }
    }
  });

  function looksLikeCart(node) {
    for (var i = 0; i < CANDIDATES.length; i++) {
      if (node.matches && node.matches(CANDIDATES[i])) return true;
      if (node.querySelector && node.querySelector(CANDIDATES[i])) return true;
    }
    return false;
  }

  function watch() {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * A handle on the outside, purely so a human can tell whether this file
   * loaded at all. "No Edit link" and "this script is not on the page" look
   * identical from the storefront, and that ambiguity has cost real time.
   * `KitCartEdit.run()` repaints on demand.
   */
  window.KitCartEdit = {
    version: 4,
    run: run,
    selectors: CANDIDATES,
    /** What this file can see right now. For diagnosing a drawer. */
    inspect: function () {
      var out = { lines: {}, sections: [] };
      CANDIDATES.forEach(function (sel) {
        var n = document.querySelectorAll(sel).length;
        if (n) out.lines[sel] = n;
      });
      out.rowsFound = candidateRows().length;
      out.variantsSeen = candidateRows().map(variantOf);
      document.querySelectorAll('[id^="shopify-section-"]').forEach(function (el) {
        out.sections.push(el.id.replace('shopify-section-', ''));
      });
      out.links = document.querySelectorAll('.' + CLASS).length;
      return out;
    },
  };
  if (document.body) watch();
  else document.addEventListener('DOMContentLoaded', watch, { once: true });
})();
